import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Annotation, AnnotationPatch, NewAnnotation } from '@ilteducation/annotations-types';
import { getAnnotations, create as createApi, destroy as destroyApi, update as updateApi } from './api';

const useAnnotationsStore = defineStore('annotations', () => {
  const annotations = ref<Record<string, Annotation>>({});
  // Track active request keys (e.g., "highlight:123" or "global:update")
  const loadingKeys = ref<Set<string>>(new Set());

  // Helper to check if specific keys are loading
  const isFetching = (keys: string | string[]) => {
    const keyArray = Array.isArray(keys) ? keys : [keys];
    return keyArray.some((k) => loadingKeys.value.has(k));
  };

  // Global loading state (true if ANY request is active)
  const isLoading = computed(() => loadingKeys.value.size > 0);

  const load = async (type: Annotation['type'], contentId: string, profileId?: string) => {
    const key = `${type}:${contentId}`;
    if (loadingKeys.value.has(key)) return;

    loadingKeys.value.add(key);
    try {
      const { data } = await getAnnotations(contentId, `${type}s`, profileId);
      // Merge into state
      data.forEach((a) => {
        annotations.value[a.id] = a;
      });
    } finally {
      loadingKeys.value.delete(key);
    }
  };

  const create = async (params: NewAnnotation, profileId?: string) => {
    const key = `${params.type}:${params.contentId}`;
    loadingKeys.value.add(key);
    try {
      const data = await createApi(params, profileId);
      annotations.value[data.id] = data;
      return data;
    } finally {
      loadingKeys.value.delete(key);
    }
  };

  const update = async (annotationId: string, params: AnnotationPatch) => {
    const key = `update:${annotationId}`;
    loadingKeys.value.add(key);
    try {
      const data = await updateApi(annotationId, params);
      annotations.value[annotationId] = data;
      return data;
    } finally {
      loadingKeys.value.delete(key);
    }
  };

  const remove = async (annotationId: string) => {
    const key = `remove:${annotationId}`;
    loadingKeys.value.add(key);
    try {
      await destroyApi(annotationId).catch((error) => {
        if ((error as { response?: { status?: number } })?.response?.status !== 404) throw error;
      });
      delete annotations.value[annotationId];
    } finally {
      loadingKeys.value.delete(key);
    }
  };

  return { annotations, isLoading, isFetching, load, create, update, remove };
});

export default useAnnotationsStore;
